import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const RefundTransactionModal = ({ isOpen, onClose, transaction }) => {
  const [refundType, setRefundType] = useState('full');
  const [refundAmount, setRefundAmount] = useState('');
  const [refundReason, setRefundReason] = useState('');
  const [refundNotes, setRefundNotes] = useState('');
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  if (!isOpen || !transaction) return null;

  const calculateRefund = () => {
    if (refundType === 'full') {
      return transaction?.sendAmount;
    } else if (refundType === 'partial' && refundAmount) {
      return parseFloat(refundAmount);
    }
    return 0;
  };

  const handleProcessRefund = async () => {
    setIsProcessing(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    setSuccessMessage(`Refund of ${calculateRefund()} ${transaction?.fromCurrency} initiated successfully. Processing time: 3-5 business days.`);
    setIsProcessing(false);
    
    // Auto-close after success
    setTimeout(() => {
      setSuccessMessage('');
      resetForm();
      onClose();
    }, 3000);
  };

  const resetForm = () => {
    setRefundType('full');
    setRefundAmount('');
    setRefundReason('');
    setRefundNotes('');
    setAgreeToTerms(false);
  };

  const refundReasons = [
    { value: '', label: 'Select a reason...' },
    { value: 'customer_request', label: 'Customer Request' },
    { value: 'wrong_recipient', label: 'Wrong Recipient' },
    { value: 'duplicate_transaction', label: 'Duplicate Transaction' },
    { value: 'service_failure', label: 'Service Failure' },
    { value: 'incorrect_amount', label: 'Incorrect Amount' },
    { value: 'fraud_prevention', label: 'Fraud Prevention' },
    { value: 'compliance_issue', label: 'Compliance Issue' },
    { value: 'other', label: 'Other (Specify in notes)' }
  ];

  const isValidPartialAmount = () => {
    if (refundType === 'partial') {
      const amount = parseFloat(refundAmount);
      return amount > 0 && amount <= transaction?.sendAmount;
    }
    return true;
  };

  const canSubmit = refundReason && agreeToTerms && isValidPartialAmount() && !isProcessing && !successMessage;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-background rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-auto">
        <div className="p-6 border-b border-border flex items-center justify-between sticky top-0 bg-background">
          <div>
            <h2 className="text-xl font-semibold text-foreground flex items-center space-x-2">
              <Icon name="RefreshCcw" size={24} className="text-error" />
              <span>Process Refund</span>
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              Transaction: {transaction?.id}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {successMessage ? (
            <div className="bg-success/10 border border-success/20 rounded-lg p-4">
              <div className="flex items-center space-x-2 text-success mb-2">
                <Icon name="CheckCircle" size={20} />
                <span className="font-medium">Refund Initiated Successfully</span>
              </div>
              <p className="text-sm text-success/80">{successMessage}</p>
            </div>
          ) : (
            <>
              <div className="bg-error/10 border border-error/20 rounded-lg p-4">
                <div className="flex items-center space-x-2 text-error mb-2">
                  <Icon name="AlertTriangle" size={16} />
                  <span className="font-medium">Important - Refund Policy</span>
                </div>
                <p className="text-sm text-error/80">
                  Processing a refund cannot be undone. Please verify all information before proceeding. 
                  Refunds typically take 3-5 business days to appear in the customer's account.
                </p>
              </div>

              <div className="bg-muted/50 rounded-lg p-4 space-y-3">
                <h3 className="font-semibold text-foreground">Transaction Information</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <div className="text-muted-foreground">Customer</div>
                    <div className="font-medium text-foreground">{transaction?.customer}</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground">Original Amount</div>
                    <div className="font-medium text-foreground">
                      {transaction?.sendAmount} {transaction?.fromCurrency}
                    </div>
                  </div>
                  <div>
                    <div className="text-muted-foreground">Transaction Date</div>
                    <div className="font-medium text-foreground">
                      {new Date(transaction?.date)?.toLocaleDateString()}
                    </div>
                  </div>
                  <div>
                    <div className="text-muted-foreground">Commission Earned</div>
                    <div className="font-medium text-foreground">
                      ${transaction?.commission}
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <label className="text-sm font-medium text-foreground">
                  Refund Type <span className="text-error">*</span>
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setRefundType('full')}
                    className={`flex flex-col items-center justify-center p-4 rounded-lg border-2 transition-all ${
                      refundType === 'full' ?'border-primary bg-primary/10' :'border-border bg-background hover:bg-muted'
                    }`}
                  >
                    <Icon 
                      name="DollarSign" 
                      size={24} 
                      className={refundType === 'full' ? 'text-primary' : 'text-muted-foreground'}
                    />
                    <span className={`text-sm mt-2 ${
                      refundType === 'full' ? 'text-primary font-medium' : 'text-foreground'
                    }`}>
                      Full Refund
                    </span>
                    <span className="text-xs text-muted-foreground mt-1">
                      {transaction?.sendAmount} {transaction?.fromCurrency}
                    </span>
                  </button>

                  <button
                    onClick={() => setRefundType('partial')}
                    className={`flex flex-col items-center justify-center p-4 rounded-lg border-2 transition-all ${
                      refundType === 'partial' ?'border-primary bg-primary/10' :'border-border bg-background hover:bg-muted'
                    }`}
                  >
                    <Icon 
                      name="Percent" 
                      size={24} 
                      className={refundType === 'partial' ? 'text-primary' : 'text-muted-foreground'}
                    />
                    <span className={`text-sm mt-2 ${
                      refundType === 'partial' ? 'text-primary font-medium' : 'text-foreground'
                    }`}>
                      Partial Refund
                    </span>
                    <span className="text-xs text-muted-foreground mt-1">
                      Custom amount
                    </span>
                  </button>
                </div>
              </div>

              {refundType === 'partial' && (
                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground">
                    Refund Amount <span className="text-error">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      value={refundAmount}
                      onChange={(e) => setRefundAmount(e?.target?.value)}
                      placeholder="0.00"
                      min="0"
                      max={transaction?.sendAmount}
                      step="0.01"
                      className="w-full px-4 py-2 pr-16 border border-border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground">
                      {transaction?.fromCurrency}
                    </span>
                  </div>
                  {refundAmount && !isValidPartialAmount() && (
                    <p className="text-xs text-error">
                      Amount must be between 0 and {transaction?.sendAmount} {transaction?.fromCurrency}
                    </p>
                  )}
                </div>
              )}

              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">
                  Refund Reason <span className="text-error">*</span>
                </label>
                <Select
                  value={refundReason}
                  onChange={(e) => setRefundReason(e?.target?.value)}
                  options={refundReasons}
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">
                  Additional Notes
                </label>
                <textarea
                  value={refundNotes}
                  onChange={(e) => setRefundNotes(e?.target?.value)}
                  placeholder="Provide any additional information about this refund..."
                  rows={3}
                  className="w-full px-4 py-2 border border-border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                />
              </div>

              <div className="bg-info/10 border border-info/20 rounded-lg p-4">
                <h4 className="font-semibold text-foreground mb-2">Refund Summary</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Refund Amount:</span>
                    <span className="font-medium text-foreground">
                      {calculateRefund()} {transaction?.fromCurrency}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Commission Impact:</span>
                    <span className="font-medium text-error">
                      -${transaction?.commission}
                    </span>
                  </div>
                  <div className="flex justify-between border-t border-info/20 pt-2 mt-2">
                    <span className="text-muted-foreground">Processing Time:</span>
                    <span className="font-medium text-foreground">3-5 business days</span>
                  </div>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <input
                  type="checkbox"
                  id="agreeToTerms"
                  checked={agreeToTerms}
                  onChange={(e) => setAgreeToTerms(e?.target?.checked)}
                  className="mt-1"
                />
                <label htmlFor="agreeToTerms" className="text-sm text-foreground cursor-pointer">
                  I confirm that I have verified all refund details and understand that this action 
                  cannot be undone. The commission for this transaction will be deducted from my 
                  earnings.
                </label>
              </div>
            </>
          )}
        </div>

        <div className="p-6 border-t border-border flex justify-end space-x-3 sticky bottom-0 bg-background">
          <Button variant="outline" onClick={onClose} disabled={isProcessing}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleProcessRefund}
            disabled={!canSubmit}
            className="bg-error hover:bg-error/90"
          >
            {isProcessing ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Processing...
              </>
            ) : (
              <>
                <Icon name="RefreshCcw" size={16} className="mr-2" />
                Process Refund
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default RefundTransactionModal;